import {
  ChangeEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import {
  useDebounceValue,
  useMediaQuery,
  useOnClickOutside,
} from 'usehooks-ts'

import { useSearchProducts } from '@entities/Product'

import { useOverlayStore } from '@features/overlay'
import useSearchModal from '@features/search/hooks/useSearchModal'
import { useSearchHistoryStore } from '@features/search/model/useSearchStore'
import { useAddressStore } from '@features/shipping-methods'

import { useAnalytics } from '@shared/lib/analytics'
import { cn } from '@shared/lib/cn'
import { useLocalizedNavigate as useNavigate } from '@shared/lib/locale'
import BackButton from '@shared/ui/back-button'
import Button from '@shared/ui/button'
import Input from '@shared/ui/input'

import { SearchResultsPopover } from './SearchResultsPopover'

import styles from './styles.module.scss'



interface SearchInputProps {
  className?: string;
  showResults?: boolean;
  openModalAfterFocus?: boolean;
  inModal?: boolean;
  focusAfterMount?: boolean;
}

export default function SearchInput (props: SearchInputProps) {
  const { t: tc } = useTranslation('common')
  const isTablet = useMediaQuery('(max-width: 1180px)')
  const {
    className,
    openModalAfterFocus = true,
    showResults = false,
    inModal,
    focusAfterMount,
  } = props
  const [ open, setOpen ] = useState<boolean>(false)
  const [ inputValue, setInputValue ] = useState('')
  const [ debouncedValue ] = useDebounceValue(inputValue, 500)
  const rootRef = useRef<HTMLDivElement>(null)
  const { open: overlayOpen, close: overlayClose } = useOverlayStore()
  const { addQuery } = useSearchHistoryStore()
  const { closeSearchModal } = useSearchModal()
  const { search: trackSearch } = useAnalytics()

  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const { shopId } = useAddressStore()
  const { 
    products: searchProducts,
    loading: searchLoading
  } = useSearchProducts({
    skip: debouncedValue.trim() === '',
    shop_id: shopId,
    searchQuery: {
      search: debouncedValue,
      filters: {}
    },
    page: 1,
    limit: 4,
  })

  const closePopover = () => {
    setOpen(false)
    overlayClose()
  }

  useOnClickOutside(rootRef as RefObject<HTMLDivElement>, closePopover)

  useEffect(() => {
    if (open) {
      overlayOpen()
    }
    else {
      overlayClose()
    }
  }, [ open ])

  useEffect(() => {
    if (focusAfterMount) {
      inputRef.current?.focus()
    }
  }, [])

  // const categoryId =
  //   searchData?.searchProducts?.edges?.[0]?.categories?.[0]?.id;
  useEffect(() => {
    if (!isTablet) {
      closeSearchModal()
    }
    else {
      setOpen(false)
    }
  }, [ isTablet ])

  const handleSearch = (value?: string) => {
    const query = (value ?? inputValue).trim()

    if (!query) return

    if (query.length > 3) {
      addQuery(query)
    }

    trackSearch(query)
    navigate(`/search?query=${query}`)
    closePopover()
    setInputValue('')
    inputRef.current?.blur()
  }

  const { openSearchModal } = useSearchModal()

  return (
    <div
      className={cn(styles.root, className, {
        [styles.active]: open,
        [styles.inModal]: inModal,
      })}
      ref={rootRef}
    >
      <div className={styles.root__top}>
        {inModal && (
          <BackButton
            className={styles.root__back}
            onClick={closeSearchModal}
          />
        )}
        <Input
          ref={inputRef}
          className={cn(styles.input, {
            [styles.active]: open,
            [styles.inModal]: inModal,
          })}
          placeholder={tc('search.button')}
          theme="grey"
          inputSize="large"
          value={inputValue}
          onInput={(e: ChangeEvent<HTMLInputElement>) => {
            setInputValue(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.code === 'Enter' || e.code === 'NumpadEnter') {
              e.preventDefault()
              handleSearch()

              if (isTablet) {
                closeSearchModal()
              }
            }
          }}
          onFocus={() => {
            if (isTablet && openModalAfterFocus) {
              openSearchModal()
              return
            }

            if (!isTablet) {
              setOpen(true)
            }
          }}
          startDecorator={
            !isTablet ? (
              <button
                className={cn(
                  'reset-button',
                  styles.startDecoratorButton,
                  className,
                )}
                type="submit"
                onClick={() => handleSearch()}
              >
                <ReactSVG src="/icons/search.svg" wrapper="span" />
              </button>
            ) : null
          }
          endDecorator={
            <div className={styles['decorator-end']}>
              {inputValue && (
                <button
                  className={cn('reset-button', styles.clear)}
                  type="button"
                  onClick={() => {
                    setInputValue('')
                  }}
                >
                  <ReactSVG
                    className={styles.clear}
                    src="/icons/cross.svg"
                    wrapper="span"
                  />
                </button>
              )}
              <Button
                className={styles.submit}
                type="submit"
                onClick={() => {
                  handleSearch()
                  if (isTablet) {
                    closeSearchModal()
                  }
                }}
              >
                {isTablet ? (
                  <ReactSVG src="/icons/search.svg" wrapper="span" />
                ) : (
                  tc('find')
                )}
              </Button>
            </div>
          }
        />
      </div>

      {/*{(open || showResults) &&
        (IS_EMPTY || HAS_RESULTS || !!history.length) && ( */}

      {(open || showResults) && searchProducts && (
        <SearchResultsPopover
          searchProducts={searchProducts}
          searchLoading={searchLoading}
          isEmpty={searchProducts.length === 0 && !!debouncedValue}
          hasResults={!!searchProducts.length}
          onClose={closePopover}
          onSearch={handleSearch}
          onHistoryClick={(val) => {
            handleSearch(val)
          }}
          inModal={inModal}
          onAddToCart={() => {
            if (debouncedValue.trim().length > 3) {
              addQuery(debouncedValue.trim())
            }
          }}
        />
      )}
    </div>
  )
}
